import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { memberAuthService } from '../clients/ApiClients';
import { ErrorAlert } from '../Components/Custom/ErrorAlert';
import { apiErrorMessage, apiErrorStatus } from '../utils/apiErrors';
import { setToken, setRefreshToken } from '../utils/TokenUtils';
import { WORKOS_STATE_KEY } from '../utils/workosConfig';

/**
 * Where AuthKit returns the browser after a homeowner signs in.
 *
 * The page's only job is to hand the authorization code to our backend
 * and put the tokens it returns where the rest of the app looks for them.
 * It deliberately does **not** exchange the code itself: that exchange
 * carries our WorkOS secret key, so it can only happen server-side.
 *
 * The tokens saved here are ours, not WorkOS's. The backend discards
 * WorkOS's session during the exchange and mints its own pair from the
 * `members` row, so everything downstream — refresh, revocation, the
 * `token_version` and `is_active` checks on every request — behaves
 * exactly as it does after a password sign-in.
 */
const WorkosCallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string>('');

  // React 18 mounts effects twice in development. The authorization code
  // is single-use, so a second exchange always fails — and would replace
  // a good result with an error. One attempt per page, enforced here.
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const code = searchParams.get('code');
    const returnedState = searchParams.get('state');
    const expectedState = sessionStorage.getItem(WORKOS_STATE_KEY);
    sessionStorage.removeItem(WORKOS_STATE_KEY);

    // AuthKit reports its own failures this way — a cancelled sign-in, a
    // rejected consent. Nothing to exchange, and not an error worth
    // alarming anyone about.
    const authkitError = searchParams.get('error');
    if (authkitError) {
      navigate('/login', { replace: true });
      return;
    }

    if (!code) {
      setError('Sign-in did not complete. Please try again.');
      return;
    }

    // The check that makes the round trip meaningful. A code that arrives
    // without the `state` we issued is one we did not ask for, which is
    // what login CSRF looks like: an attacker feeding a victim a code
    // from the attacker's own AuthKit session so the victim lands in the
    // attacker's account.
    if (!returnedState || returnedState !== expectedState) {
      setError('Sign-in could not be verified. Please try again.');
      return;
    }

    const exchange = async () => {
      try {
        const response = await memberAuthService.memberWorkosLogin({
          memberWorkosLogin: { code },
        });

        const data = response.data as {
          access_token?: string;
          refresh_token?: string;
        };

        if (!data.access_token) {
          throw new Error('Access token not found in response');
        }

        // Session-scoped rather than remembered. The "remember me"
        // checkbox lived on the login form and this flow left that page
        // behind; defaulting to the narrower choice is the right way to
        // be wrong.
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token, false);
        }
        setToken(data.access_token, false);

        const savedLocation = sessionStorage.getItem('returnTo');
        if (savedLocation) {
          const { pathname, search, hash } = JSON.parse(savedLocation);
          sessionStorage.removeItem('returnTo');
          navigate(pathname + search + hash, { replace: true });
        } else {
          navigate('/home', { replace: true });
        }
      } catch (err) {
        // A 401 here has two causes and they need different advice.
        //
        // The backend deliberately does not distinguish them in its
        // response — telling an unauthenticated caller whether an address
        // is registered is exactly what it is avoiding — so this message
        // has to cover both: an address with no member row at all, and a
        // member who has not yet accepted their invitation. "Contact your
        // property manager" is the right next step either way, and for a
        // homeowner it is a far better instruction than "invalid
        // credentials", which sends them to reset a password that was
        // never the problem.
        console.error('WorkOS login failed:', err);
        if (apiErrorStatus(err) === 401) {
          setError(
            "We couldn't sign you in. If you have not set up your account yet, " +
              'check your email for your invitation — otherwise please contact ' +
              'your property manager.'
          );
        } else {
          setError(
            apiErrorMessage(
              err,
              "Couldn't sign you in right now. Please try again."
            )
          );
        }
      }
    };

    exchange();
  }, [navigate, searchParams]);

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-8 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        {error ? (
          <>
            <ErrorAlert className="text-left">{error}</ErrorAlert>
            <button
              type="button"
              onClick={() => navigate('/login', { replace: true })}
              className="mt-4 text-sm font-semibold text-sky-800 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Signing you in…
          </p>
        )}
      </div>
    </div>
  );
};

export default WorkosCallbackPage;
